import { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Filter, Trash2, ArrowRight, UserPlus, Pencil, UserCheck, UserX, CreditCard } from "lucide-react";
import ClientCardDialog from "@/components/ClientCardDialog";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import useAutoRefresh from "@/lib/useAutoRefresh";

// Ledger timestamps are always rendered in Naples-EDT because the app is
// Naples-specific — a dispatcher checking from another timezone still needs
// to see the local wall-clock of the operation.
const TS_FMT = { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "America/New_York" };
const fmt = (v) => `$${Number(v || 0).toFixed(2)}`;

function BalancePill({ balance }) {
  const b = Number(balance || 0);
  if (b > 0.01) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold font-mono bg-red-500/15 text-red-300 border border-red-500/40">
        Debe {fmt(b)}
      </span>
    );
  }
  if (b < -0.01) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold font-mono bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
        Crédito {fmt(Math.abs(b))}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono text-muted-foreground border border-border bg-secondary/40">
      Sin saldo
    </span>
  );
}

function ClientDrawer({ phone, onClose, onChanged }) {
  const { user } = useAuth();
  const canSeeCards = user?.role === "admin" || (user?.role === "dispatcher" && user?.card_access);
  const [cardOpen, setCardOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ type: "charge", amount: "", note: "" });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!phone) return;
    try {
      const { data } = await api.get("/clients/lookup", { params: { phone } });
      setSummary(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [phone]);

  useEffect(() => { reload(); }, [reload]);

  const submit = async () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Monto inválido"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${encodeURIComponent(phone)}/entries`, {
        type: form.type, amount: amt, note: form.note,
      });
      setSummary(data);
      setForm({ type: "charge", amount: "", note: "" });
      toast.success(form.type === "charge" ? "Deuda registrada" : "Pago registrado");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("¿Eliminar este movimiento? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/clients/entries/${id}`);
      toast.success("Movimiento eliminado");
      await reload();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={!!phone} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-2xl" data-testid="client-drawer">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center flex-wrap gap-2">
            {summary?.passenger_name || "Cliente sin nombre"}
            <span className="text-sm text-muted-foreground font-mono">· {summary?.phone || phone}</span>
            {canSeeCards && (
              <Button
                size="sm" variant="outline" onClick={() => setCardOpen(true)}
                className="h-7 border-primary/50 text-primary hover:bg-primary/10 text-xs font-bold ml-1"
                data-testid="client-card-btn"
                title="Ver método de pago (tarjeta)"
              >
                <CreditCard className="w-3.5 h-3.5 mr-1" /> Método de pago
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        {canSeeCards && (
          <ClientCardDialog phone={phone} clientName={summary?.passenger_name} open={cardOpen} onOpenChange={setCardOpen} />
        )}

        <div className="flex items-center justify-between gap-3 pb-3 border-b border-border/50">
          <BalancePill balance={summary?.balance} />
          <div className="text-xs text-muted-foreground font-mono">
            {summary?.entries?.length ?? 0} movimientos
          </div>
        </div>

        {/* New entry form */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
          <div className="col-span-2 md:col-span-1">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tipo</Label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger className="bg-input border-border h-9" data-testid="client-entry-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="charge">Deuda (cargo)</SelectItem>
                <SelectItem value="payment">Pago</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Monto</Label>
            <Input
              type="number" step="0.01" min="0" inputMode="decimal"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              className="bg-input border-border h-9 font-mono"
              placeholder="0.00"
              data-testid="client-entry-amount"
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Nota</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              className="bg-input border-border h-9"
              placeholder="Opcional"
              data-testid="client-entry-note"
            />
          </div>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-9"
            data-testid="client-entry-submit"
          >
            <Plus className="w-4 h-4 mr-1" /> Registrar
          </Button>
        </div>

        {/* Entries table */}
        <div className="max-h-[45vh] overflow-y-auto -mx-2">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead>Fecha</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead>Nota / Viaje</TableHead>
                <TableHead>Por</TableHead>
                {user?.role === "admin" && <TableHead className="w-8" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(summary?.entries || []).map((e) => (
                <TableRow key={e.id} className="border-border">
                  <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString("es-ES", TS_FMT)}
                  </TableCell>
                  <TableCell>
                    <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] font-bold border ${e.type === "charge" ? "text-red-300 border-red-500/40 bg-red-500/10" : "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"}`}>
                      {e.type === "charge" ? "DEUDA" : "PAGO"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold">
                    <span className={e.type === "charge" ? "text-red-300" : "text-emerald-300"}>
                      {e.type === "charge" ? "+" : "−"}{fmt(e.amount)}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {e.trip_number && (
                      <span className="font-mono text-white/70 mr-1">#{e.trip_number}</span>
                    )}
                    {e.note}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.created_by_name || "—"}</TableCell>
                  {user?.role === "admin" && (
                    <TableCell className="text-right">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => removeEntry(e.id)}
                        className="border-border h-6 w-6 p-0 text-destructive hover:text-destructive"
                        title="Eliminar movimiento"
                        data-testid={`client-entry-delete-${e.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!summary?.entries?.length && (
                <TableRow className="border-border">
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8 text-sm">
                    Sin movimientos aún.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewClientEntryDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ phone: "", passenger_name: "", type: "charge", amount: "", note: "" });
  const [busy, setBusy] = useState(false);
  // Live lookup so the dispatcher sees the client's name and current balance
  // BEFORE hitting Registrar. `null` = not looked up yet, `{}` = looked up but
  // unknown phone, otherwise the summary payload.
  const [lookup, setLookup] = useState(null);
  const [nameAutoFilled, setNameAutoFilled] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ phone: "", passenger_name: "", type: "charge", amount: "", note: "" });
      setLookup(null);
      setNameAutoFilled(false);
    }
  }, [open]);

  // Debounced lookup by phone. Autofills the name field only if the user
  // hasn't typed anything, so we never overwrite a manual entry.
  useEffect(() => {
    if (!open) return;
    const phone = form.phone.trim();
    if (phone.length < 6) { setLookup(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients/lookup", { params: { phone } });
        setLookup(data);
        setForm((f) => {
          if (f.passenger_name || !data?.passenger_name) return f;
          setNameAutoFilled(true);
          return { ...f, passenger_name: data.passenger_name };
        });
      } catch (_) { setLookup(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [open, form.phone]);

  const submit = async () => {
    const phone = form.phone.trim();
    const amt = Number(form.amount);
    if (!phone || phone.length < 6) { toast.error("Teléfono inválido"); return; }
    if (!amt || amt <= 0) { toast.error("Monto inválido"); return; }
    setBusy(true);
    try {
      await api.post(`/clients/${encodeURIComponent(phone)}/entries`, {
        type: form.type,
        amount: amt,
        note: form.note,
        passenger_name: form.passenger_name,
      });
      toast.success(form.type === "charge" ? "Deuda registrada" : "Pago registrado");
      onCreated?.(phone);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="new-client-entry-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            Registrar movimiento a nuevo cliente
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Teléfono *</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className="bg-input border-border font-mono"
              placeholder="+1 555 555 5555"
              data-testid="new-client-phone"
            />
            {lookup && (
              <div className="mt-1 flex items-center gap-2 text-[11px] font-mono" data-testid="new-client-lookup">
                {lookup.passenger_name ? (
                  <span className="text-emerald-300">Encontrado en historial</span>
                ) : (
                  <span className="text-muted-foreground">Sin registros previos con este teléfono</span>
                )}
                {Math.abs(lookup.balance || 0) > 0.01 && (
                  <span className={lookup.balance > 0 ? "text-red-300" : "text-emerald-300"}>
                    · saldo actual {lookup.balance > 0 ? "debe" : "crédito"} ${Math.abs(lookup.balance).toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              Nombre
              {nameAutoFilled && (
                <span className="normal-case text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="new-client-name-auto">
                  auto
                </span>
              )}
            </Label>
            <Input
              value={form.passenger_name}
              onChange={(e) => { setForm((f) => ({ ...f, passenger_name: e.target.value })); setNameAutoFilled(false); }}
              className="bg-input border-border"
              placeholder="Opcional (se autocompleta si el teléfono ya existe)"
              data-testid="new-client-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger className="bg-input border-border" data-testid="new-client-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="charge">Deuda (cargo)</SelectItem>
                  <SelectItem value="payment">Pago</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Monto *</Label>
              <Input
                type="number" step="0.01" min="0" inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                className="bg-input border-border font-mono"
                placeholder="0.00"
                data-testid="new-client-amount"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Nota</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              className="bg-input border-border"
              placeholder="Opcional (ej: 'Viaje de anoche impago')"
              data-testid="new-client-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
            data-testid="new-client-submit"
          >
            <Plus className="w-4 h-4 mr-1" /> Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Tiny corner counter for filter buttons (how many clients match that filter).
function CountBadge({ n, testId }) {
  if (n === undefined || n === null) return null;
  return (
    <span
      data-testid={testId}
      className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-zinc-950 text-white border border-border text-[9px] font-mono font-bold leading-[16px] text-center shadow"
    >
      {n}
    </span>
  );
}

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [onlyBalance, setOnlyBalance] = useState(true);
  const [named, setNamed] = useState("all"); // all | yes | no
  const [counts, setCounts] = useState(null);
  const loadCounts = useCallback(async () => {
    try { const { data } = await api.get("/clients/counts"); setCounts(data); } catch { /* badge only */ }
  }, []);
  const [loading, setLoading] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [creating, setCreating] = useState(false);
  // Edit / delete-confirm state. Kept as small ad-hoc objects to keep the
  // component lean; formal separate dialogs weren't worth another file for
  // this simple flow.
  const [editing, setEditing] = useState(null);       // { phone, passenger_name }
  const [deleteTarget, setDeleteTarget] = useState(null); // { phone, passenger_name }

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: { q, only_with_balance: onlyBalance, named: named === "all" ? undefined : named } });
      setRows(data);
      loadCounts();
    } catch (e) { if (!silent) toast.error(formatApiError(e)); }
    finally { if (!silent) setLoading(false); }
  }, [q, onlyBalance, named, loadCounts]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh silently every 15s + on tab focus so newly-registered debts
  // or payments from another dispatcher appear without a manual reload.
  useAutoRefresh(() => load({ silent: true }), 15000);

  const totalDebt = rows.reduce((a, r) => a + Math.max(0, r.balance), 0);
  const totalCredit = rows.reduce((a, r) => a + Math.max(0, -r.balance), 0);

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-4 max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">Ledger de clientes</h1>
            <p className="text-sm text-muted-foreground">
              Balance por teléfono: <span className="text-red-300 font-mono">debe</span> lo que tiene pendiente de pagar,{" "}
              <span className="text-emerald-300 font-mono">crédito</span> es pago adelantado.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Total en deuda</div>
              <div className="text-lg font-black font-mono text-red-300" data-testid="clients-total-debt">{fmt(totalDebt)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Total crédito</div>
              <div className="text-lg font-black font-mono text-emerald-300" data-testid="clients-total-credit">{fmt(totalCredit)}</div>
            </div>
            <Button
              onClick={() => setCreating(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold ml-2"
              data-testid="clients-new-entry"
              title="Registrar deuda o pago a un cliente que aún no aparece en la lista"
            >
              <UserPlus className="w-4 h-4 mr-1" /> Nuevo movimiento
            </Button>
          </div>
        </div>

        <Card className="p-3 bg-card border-border flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o teléfono"
              className="pl-8 bg-input border-border"
              data-testid="clients-search"
            />
          </div>
          <Button
            variant={onlyBalance ? "default" : "outline"}
            onClick={() => setOnlyBalance((v) => !v)}
            className={`relative ${onlyBalance ? "bg-primary text-primary-foreground font-bold" : ""}`}
            data-testid="clients-only-balance"
          >
            <Filter className="w-4 h-4 mr-1" /> Solo con saldo
            <CountBadge n={counts?.with_balance} testId="clients-count-balance" />
          </Button>
          <Button
            variant={named === "yes" ? "default" : "outline"}
            onClick={() => setNamed((v) => (v === "yes" ? "all" : "yes"))}
            className={`relative ${named === "yes" ? "bg-emerald-500 text-black font-bold hover:bg-emerald-400" : ""}`}
            data-testid="clients-named"
            title="Solo teléfonos que tienen nombre de cliente"
          >
            <UserCheck className="w-4 h-4 mr-1" /> Agendados
            <CountBadge n={counts?.named} testId="clients-count-named" />
          </Button>
          <Button
            variant={named === "no" ? "default" : "outline"}
            onClick={() => setNamed((v) => (v === "no" ? "all" : "no"))}
            className={`relative ${named === "no" ? "bg-amber-500 text-black font-bold hover:bg-amber-400" : ""}`}
            data-testid="clients-unnamed"
            title="Solo teléfonos sin nombre"
          >
            <UserX className="w-4 h-4 mr-1" /> No agendados
            <CountBadge n={counts?.unnamed} testId="clients-count-unnamed" />
          </Button>
        </Card>

        <Card className="bg-card border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead>Cliente</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead className="text-right">Movs.</TableHead>
                <TableHead>Última act.</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow className="border-border">
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">Cargando…</TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow className="border-border">
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-12" data-testid="clients-empty">
                    {named === "yes" ? "Ningún cliente agendado con estos filtros." : named === "no" ? "Ningún teléfono sin nombre con estos filtros." : onlyBalance ? "Ningún cliente con saldo pendiente." : "Sin clientes registrados."}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow
                  key={r.phone}
                  className="border-border hover:bg-secondary/30 cursor-pointer"
                  onClick={() => setSelectedPhone(r.phone)}
                  data-testid={`client-row-${r.phone}`}
                >
                  <TableCell className="font-bold">{r.passenger_name || <span className="text-muted-foreground italic">Sin nombre</span>}</TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">{r.phone}</TableCell>
                  <TableCell><BalancePill balance={r.balance} /></TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground font-mono">{r.entries_count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.last_activity ? new Date(r.last_activity).toLocaleString("es-ES", TS_FMT) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setEditing({ phone: r.phone, passenger_name: r.passenger_name || "" }); }}
                        data-testid={`client-edit-${r.phone}`}
                        title="Editar nombre y teléfono"
                        className="p-1.5 rounded hover:bg-primary/15 text-muted-foreground hover:text-primary"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget({ phone: r.phone, passenger_name: r.passenger_name || "" }); }}
                        data-testid={`client-delete-${r.phone}`}
                        title="Eliminar cliente"
                        className="p-1.5 rounded hover:bg-red-500/15 text-muted-foreground hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <ArrowRight className="w-4 h-4 text-muted-foreground/50 ml-1" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <ClientDrawer
          phone={selectedPhone}
          onClose={() => setSelectedPhone(null)}
          onChanged={load}
        />

        <NewClientEntryDialog
          open={creating}
          onClose={() => setCreating(false)}
          onCreated={(phone) => { load(); setSelectedPhone(phone); }}
        />

        <EditClientDialog
          target={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />

        <AlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
        >
          <AlertDialogContent data-testid="client-delete-dialog" className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar cliente</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>
                    Vas a eliminar a <span className="font-bold text-white">{deleteTarget?.passenger_name || "Sin nombre"}</span>
                    {" "}({<span className="font-mono">{deleteTarget?.phone}</span>}) del registro de clientes y su historial de deudas/créditos.
                  </p>
                  <p className="text-muted-foreground">
                    Los <b>viajes anteriores</b> asociados a este teléfono <b>no se modifican</b> — mantienen el nombre y teléfono que tenían. Solo desaparece del listado de clientes.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="client-delete-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                data-testid="client-delete-confirm"
                onClick={async () => {
                  const t = deleteTarget;
                  setDeleteTarget(null);
                  try {
                    await api.delete(`/clients/${encodeURIComponent(t.phone)}`);
                    toast.success("Cliente eliminado");
                    load();
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Sí, eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}

function EditClientDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({ passenger_name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (target) setForm({ passenger_name: target.passenger_name || "", phone: target.phone || "" });
  }, [target]);
  if (!target) return null;
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="client-edit-dialog" className="bg-card border-border">
        <DialogHeader>
          <DialogTitle>Editar cliente</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Nombre</Label>
            <Input
              data-testid="client-edit-name"
              value={form.passenger_name}
              onChange={(e) => setForm((f) => ({ ...f, passenger_name: e.target.value }))}
              className="bg-input border-border"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Teléfono</Label>
            <Input
              data-testid="client-edit-phone"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className="bg-input border-border font-mono"
            />
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Los viajes anteriores <b>no</b> cambian — mantienen el nombre y teléfono que tenían al momento del viaje. La edición sólo afecta al registro de clientes y al autocompletado de los futuros viajes.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            disabled={busy}
            data-testid="client-edit-save"
            onClick={async () => {
              setBusy(true);
              try {
                const payload = {};
                if ((form.passenger_name || "").trim() !== (target.passenger_name || "")) payload.passenger_name = form.passenger_name;
                if ((form.phone || "").trim() !== (target.phone || "")) payload.phone = form.phone;
                if (Object.keys(payload).length === 0) { onClose(); return; }
                await api.patch(`/clients/${encodeURIComponent(target.phone)}`, payload);
                toast.success("Cliente actualizado");
                onSaved?.();
              } catch (e) { toast.error(formatApiError(e)); }
              finally { setBusy(false); }
            }}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
